import React, { useState, useEffect } from 'react';
import { Container, Paper, Box, TextField, Button, Snackbar, Alert, FormControlLabel, Switch, Tooltip } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { RouteDefine } from '../../routes/index.tsx/RouteDefine';
import { useApi } from '../../services/useApi';
import LoadingOverlay from '../../components/LoadingOverlay';
import { PageTitle, SectionTitle } from '../../components/TypographyStyles';
import { ApiSuccessResponse, ApiErrorResponse, isErrorResponse, ConnectionData } from '../../services/api.types';
import { authService } from '../../services/auth.service';
import { Layout } from '../../components/layout';
  
interface ConnectionPayload {
  dinero: {
    clientId: string;
    clientSecret: string;
    apiKey: string;
    organizationId: string;
  };
  economic: {
    token: string;
    name?: string;
    number?: string;
  };
}

type ConnectionResponse = ApiSuccessResponse<ConnectionData> | ApiErrorResponse;

const SetupConnectionPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { loading, error, post } = useApi();
  const [dineroClientId, setDineroClientId] = useState('');
  const [dineroClientSecret, setDineroClientSecret] = useState('');
  const [dineroApiKey, setDineroApiKey] = useState('');
  const [dineroOrganizationId, setDineroOrganizationId] = useState('');
  const [economicToken, setEconomicToken] = useState('');
  const [economicName, setEconomicName] = useState('');
  const [economicNumber, setEconomicNumber] = useState('');
  const [rememberConnection, setRememberConnection] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isGeneratingSetup, setIsGeneratingSetup] = useState(false);
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string | React.ReactNode;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({
    open: false,
    message: '',
    severity: 'success'
  });

  // Redirect to home if already connected
  useEffect(() => {
    if (authService.isUserAuthenticated()) {
      navigate(`/${RouteDefine.home}`);
    }
  }, [navigate]);

  // Load saved connection info if exists
  useEffect(() => {
    let tokenFromUrl = new URLSearchParams(window.location.search).get('token');
    if(tokenFromUrl){
      setEconomicToken(tokenFromUrl);
    }
    const loadConnectionInfo = () => {
      try {
        const savedInfo = localStorage.getItem('connection_info_login');
        if (savedInfo) {
          const connectionInfo = JSON.parse(savedInfo);
          setDineroClientId(connectionInfo.dinero.clientId);
          setDineroClientSecret(connectionInfo.dinero.clientSecret);
          setDineroApiKey(connectionInfo.dinero.apiKey);
          setDineroOrganizationId(connectionInfo.dinero.organizationId);
          if(!tokenFromUrl){
            setEconomicToken(connectionInfo.economic.token);
          }
          setEconomicName(connectionInfo.economic.name || '');
          setEconomicNumber(connectionInfo.economic.number || '');
          setRememberConnection(true);
        }
      } catch (error) {
        console.error('Error loading connection info:', error);
      }
    };

    loadConnectionInfo();
  }, []);

  const handleCloseNotification = () => {
    setNotification(prev => ({ ...prev, open: false }));
  };

  const saveConnectionInfo = (info: ConnectionPayload) => {
    localStorage.setItem('connection_info_login', JSON.stringify(info));
  };

  const handleConnect = async (e: React.MouseEvent) => {
    e.preventDefault();
    setIsConnecting(true);

    try {
      // Validate required fields
      if (!dineroClientId || !dineroClientSecret || !dineroApiKey || !dineroOrganizationId || !economicToken) {
        setNotification({
          open: true,
          message: 'Please fill in all required fields',
          severity: 'error'
        });
        return;
      }

      const payload: ConnectionPayload = {
        dinero: {
          clientId: dineroClientId,
          clientSecret: dineroClientSecret,
          apiKey: dineroApiKey,
          organizationId: dineroOrganizationId,
        },
        economic: {
          token: economicToken
        },
      };

      // Call API using useApi service
      const response = await post<ConnectionResponse>('/main/connection', payload);
      let timeout = 0;
      if (response.data.success) {
        // Save to storage if remember connection is enabled
        if (rememberConnection) {
          saveConnectionInfo(payload);
        } else {
          localStorage.removeItem('connection_info_login');
        }
        
        authService.setConnectionData(response.data.data);

        // Set connection data in auth service
        authService.setToken(response.data.data.token);
        let generateSetup = response.data.data.economic.generateSetup;
        // console.log(response.data.data.economic.setup);
        // Show success message
        setNotification({
          open: true,
          message: response.data.message || 'Connection successful!',
          severity: 'success'
        });
        if(generateSetup){
          timeout = 10000;
          //generate setup and show notification
          setTimeout(() => {
            setNotification({
              open: true,
              message: 'Generating setup...',
              severity: 'success'
            });
          }, 1000);
          setIsGeneratingSetup(true);
          const response = await post<ApiSuccessResponse<any>>('/main/generate-setup',{});
          const setupData = response.data.data;
          const setupMessage = Object.entries(setupData)
            .map(([key, value]) => {
              const status = String(value).includes('already exists') ? 'success' : 'warning';
              const color = status === 'success' ? '#4caf50' : '#ff9800';
              return `<div style="color: ${color}">${key}: ${value}</div>`;
            })
            .join('');
          
          setNotification({
            open: true,
            message: <div dangerouslySetInnerHTML={{ __html: setupMessage }} />,
            severity: 'info'
          });
          
          timeout = 10000;
          // Show countdown message
          let countdown = timeout/1000;
          const countdownInterval = setInterval(() => {
            countdown--;
            if (countdown > 0) {
              setNotification({
                open: true,
                message: <div>
                  <div dangerouslySetInnerHTML={{ __html: setupMessage }} />
                  <div style={{ marginTop: '10px', fontWeight: 'bold' }}>
                    Redirecting in {countdown} seconds...
                  </div>
                  <div style={{ marginTop: '10px' }}>
                    <a href={from} style={{ color: 'blue', textDecoration: 'underline' }}>Click here to redirect now</a>
                  </div>
                </div>,
                severity: 'info'
              });
            } else {
              clearInterval(countdownInterval);
            }
          }, 1000);
        }
        // Navigate to home or the previously requested page
        const from = (location.state as any)?.from?.pathname || `/${RouteDefine.home}`;
        setTimeout(() => {
          navigate(from);
        }, timeout + 1000);
      } else {
        // Handle validation errors
        setNotification({
          open: true,
          message: response.data.message || 'Connection failed',
          severity: 'error'
        });
      }
    } catch (error: any) {
      console.error('Connection error:',error.errors.message);
      setNotification({
        open: true,
        message: error?.errors?.message || error.message || 'An error occurred while connecting',
        severity: 'error'
      });
    } finally {
      setIsConnecting(false);
      // setIsGeneratingSetup(false);
    }
  };

  return (
    <Layout>
      <Container maxWidth="md">
        <LoadingOverlay open={loading || isConnecting || isGeneratingSetup} />
        <Box sx={{ py: 4, minHeight: 'calc(100vh - 200px)' }}>
          <PageTitle 
            align="center" 
            sx={{ 
              fontSize: { xs: '2rem', md: '2.5rem' },
              fontWeight: 700,
              color: 'primary.main',
              textShadow: '2px 2px 4px rgba(0,0,0,0.1)',
              mb: 4
            }}
          >
            Connection Setup
          </PageTitle>

          <Paper elevation={3} sx={{ p: 3, mt: 4 }}>
            <SectionTitle>Dinero</SectionTitle>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
              <TextField 
                fullWidth 
                label="Client ID" 
                value={dineroClientId} 
                onChange={(e) => setDineroClientId(e.target.value)}
                required
                disabled={loading || isConnecting}
                size="small"
              />
              <TextField 
                fullWidth 
                label="Client Secret" 
                value={dineroClientSecret} 
                onChange={(e) => setDineroClientSecret(e.target.value)}
                required
                type="password"
                disabled={loading || isConnecting}
                size="small"
              />
              <TextField 
                fullWidth 
                label="API Key" 
                value={dineroApiKey} 
                onChange={(e) => setDineroApiKey(e.target.value)}
                required
                type="password"
                disabled={loading || isConnecting}
                size="small"
              />
              <TextField 
                fullWidth 
                label="Organization ID" 
                value={dineroOrganizationId} 
                onChange={(e) => setDineroOrganizationId(e.target.value)}
                required
                disabled={loading || isConnecting}
                size="small"
              />
            </Box>

            <SectionTitle>e-conomic</SectionTitle>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                <TextField 
                  fullWidth 
                  label="Token" 
                  value={economicToken} 
                  onChange={(e) => setEconomicToken(e.target.value)}
                  required
                  disabled={loading || isConnecting}
                  size="small"
                />
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => window.open(process.env.REACT_APP_API_ECONOMIC_REQUEST_ACCESS_URL, '_blank')}
                  disabled={loading || isConnecting}
                  sx={{ minWidth: 120, height: 40 }}
                >
                  Get Token
                </Button>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={rememberConnection}
                    onChange={(e) => setRememberConnection(e.target.checked)}
                    disabled={loading || isConnecting}
                  />
                }
                label="Remember connection"
              />
              <Tooltip title="Connect to Dinero and e-conomic">
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleConnect}
                  disabled={loading || isConnecting}
                  sx={{ minWidth: 120 }}
                >
                  {isConnecting ? 'Connecting...' : 'Connect'}
                </Button>
              </Tooltip>
            </Box>
          </Paper>
        </Box>

        <Snackbar
          open={notification.open}
          autoHideDuration={10000}
          onClose={handleCloseNotification}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert onClose={handleCloseNotification} severity={notification.severity} sx={{ width: '100%' }}>
            {notification.message}
          </Alert>
        </Snackbar>
      </Container>
    </Layout>
  );
};

export default SetupConnectionPage; 